<template>
  <skeleton-preloader-bonuses-page v-if="isBonusesLoading" />
  <div v-else>
    <atomic-bread-crumbs :crumbs="crumbs" />
    <profile-section-header
      :title="getLayoutContent('profileSidebar.profileLinks.profile')"
      :subtitle="getLayoutContent('profileSidebar.menuTitle.bonuses')"
      @back="router.back()"
    />

    <div class="bonuses-page">
      <div class="bonuses-page__container">
        <client-only>
          <div
            v-if="profile"
            class="bonuses-page__banner"
          >
            <div class="bonuses-page__banner-content">
              <div class="bonuses-page__banner-heading">
                <span class="bonuses-page__banner-heading-title">
                  {{ getBonusContent('title') }}
                </span>
              </div>
              <div class="bonuses-page__banner-info-group">
                <div class="bonuses-page__banner-info">
                  <div class="bonuses-page__banner-title">
                    {{ getBonusContent('freeSpins.title') }}
                  </div>
                  <div class="bonuses-page__banner-value">
                    {{ freeSpinsValue }}
                  </div>
                </div>
                <div class="bonuses-page__banner-divider" />
                <div class="bonuses-page__banner-info">
                  <div class="bonuses-page__banner-title">
                    {{ getBonusContent('cashBonuses.title') }}
                  </div>
                  <div class="bonuses-page__banner-value">
                    {{ playerBonusesSum.baseCurrencySymbol }}
                    {{ playerBonusesSum.baseCurrencyBonusTotal }}
                  </div>
                </div>
              </div>
            </div>
            <atomic-image
              class="bonuses-page__picture"
              src="/img/uploads/bonus-card-img.png"
            />
          </div>
          <div
            v-if="profile"
            class="bonuses-page__bonus-info"
          >
            <tab-tabs
              ref="tabsRef"
              :items="bonusesTabs"
              has-count
              :default-tab="currentInfoBonusTab"
              @change-tab="changeTab"
            />

            <template v-if="currentInfoBonusTab === 'cashBonuses'">
              <bonus-active
                v-if="playerBonuses.length"
                bonus-type="bonus"
                :content="bonusesContent"
                :default-locale-content="defaultLocaleBonusesContent"
              />

              <atomic-custom-empty
                v-else
                :title="getLayoutContent('empty.noDataCashBonuses.title')"
                :description="getLayoutContent('empty.noDataCashBonuses.description')"
                :image="getLayoutContent('empty.noDataCashBonuses.img')"
              />
            </template>

            <template v-if="currentInfoBonusTab === 'freeSpins'">
              <bonus-active
                v-if="playerFreeSpins.length"
                bonus-type="free-spin"
                :content="bonusesContent"
                :default-locale-content="defaultLocaleBonusesContent"
              />

              <atomic-custom-empty
                v-else
                :title="getLayoutContent('empty.noDataFeeSpinsBonuses.title')"
                :description="getLayoutContent('empty.noDataFeeSpinsBonuses.description')"
                :image="getLayoutContent('empty.noDataFeeSpinsBonuses.img')"
              />
            </template>
          </div>
        </client-only>

        <group-promotions />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IWelcomeBonusesPage, IPageBonusesContent } from '~/types';

interface IPageContent {
  currentLocaleData: Maybe<IWelcomeBonusesPage>;
  defaultLocaleData: Maybe<IWelcomeBonusesPage>;
}

const loadingStore = useLoadingStore();
const { playerBonuses, playerFreeSpins, playerBonusesSum } = storeToRefs(useBonusStore());
const { profile } = storeToRefs(useProfileStore());
const { layoutData, defaultLocaleLayoutData } = useGlobalStore();
const { getPlayerBonuses, getPlayerFreeSpins, getPlayerBonusesSum } = useBonusStore();
const { setPageMeta, getContent } = useProjectMethods();
const router = useRouter();

const promotionsContent = ref<Maybe<IWelcomeBonusesPage>>();
const defaultLocalePromotionsContent = ref<Maybe<IWelcomeBonusesPage>>();

const currentInfoBonusTab = ref('cashBonuses');
const bonusesContent = ref<Maybe<IPageBonusesContent>>();
const defaultLocaleBonusesContent = ref<Maybe<IPageBonusesContent>>();

const crumbs = computed(() => {
  return [
    { label: getLayoutContent('profileSidebar.menuTitle.bonuses'), to: '/profile/info' },
    { label: getLayoutContent('profileSidebar.profileLinks.profile'), to: '/bonuses' },
  ];
});

const freeSpinsValue = computed(() => {
  if (!profile.value) return 0;

  return (
    playerFreeSpins.value?.reduce((acc, item) => {
      return acc + item.count;
    }, 0) || 0
  );
});

const setContentData = (contentData: Maybe<IPageContent>): void => {
  promotionsContent.value = contentData?.currentLocaleData;
  defaultLocalePromotionsContent.value = contentData?.defaultLocaleData;
};

const changeTab = (id: string) => {
  currentInfoBonusTab.value = id;
};

const { getContentData: getWelcomeContentData } = useContentLogic<IWelcomeBonusesPage>({
  contentKey: 'promotionsContent',
  contentRoute: ['pages', 'welcome-bonuses'],
  isPage: false,
});

const { data } = await useLazyAsyncData(getWelcomeContentData);

if (data.value) setContentData(data.value);

watch(data, () => {
  setContentData(data.value);
});
const isBonusesLoading = ref(false);

const getBonusesData = async () => {
  isBonusesLoading.value = true;

  try {
    await Promise.all([getPlayerBonuses(), getPlayerFreeSpins(), getPlayerBonusesSum()]);
  }
  catch (error) {
    console.error('Failed to fetch bonus data:', error);
  }
  finally {
    isBonusesLoading.value = false;
  }
};

const { getContentData: getBonusesContentData } = useContentLogic<IPageBonusesContent>({
  contentKey: 'profileBonusesContent',
  contentRoute: ['profile', 'bonuses'],
  isPage: false,
});

const { data: bonusesContentData } = await useLazyAsyncData(getBonusesContentData);

const getBonusContent = (key: string) => getContent(bonusesContent.value, defaultLocaleBonusesContent.value, key);

const getLayoutContent = (key: string) => getContent(layoutData, defaultLocaleLayoutData, key);

const loading = computed(() => {
  return !data.value || !bonusesContentData.value;
});

watchEffect(() => {
  loadingStore.setLoadingState(loading.value);
});
const tabConfig = [
  { id: 'cashBonuses', contentKey: 'cashBonuses.title', source: () => playerBonuses.value },
  { id: 'freeSpins', contentKey: 'freeSpins.title', source: () => playerFreeSpins.value },
];

const bonusesTabs = computed(() => {
  return tabConfig.map(({ id, contentKey, source }) => ({
    id,
    title: getBonusContent(contentKey),
    count: source()?.length || 0,
  }));
});

watch(
  bonusesContentData,
  () => {
    if (bonusesContentData.value) {
      setPageMeta(bonusesContentData.value?.currentLocaleData?.pageMeta);
      bonusesContent.value = bonusesContentData.value?.currentLocaleData;
      defaultLocaleBonusesContent.value = bonusesContentData.value?.defaultLocaleData;
    }
  },
  { immediate: true },
);

onMounted(async () => {
  if (profile.value) {
    await getBonusesData();
  }
});
</script>

<style src="~/assets/styles/pages/bonuses.scss" lang="scss" />
